import { ReactComponent as ChevronRight } from '../../../../assets/icons/chevronRight.svg';
import { useState, useEffect, useCallback } from "react";
import { ReactComponent as CreditCard } from "../../../../assets/icons/cash.svg"; 
import { PAYMENT_PROVIDERS, PAYMENT_PROVIDER_INFO } from "../../../../constants/paymentProviders";
import { useNavigate } from "react-router-dom";

const Rebill = ({paymentProvider, setPaymentProvider, order, isExperience = false, isOnlyProvider = false, isUsProvider = false}) => {
  const providerCode = !isUsProvider ? PAYMENT_PROVIDERS.REBILL : PAYMENT_PROVIDERS.REBILL_US;
  const [sdkMounted, setSDKMounted] = useState(null);
  const [productId, setProductId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [rebillScriptReady, setRebillScriptReady] = useState(false);
  const navigate = useNavigate();
  
  // Automatically set payment provider to REBILL if isOnlyProvider is true
  useEffect(() => {
    if (isOnlyProvider && paymentProvider !== providerCode) {
      setPaymentProvider(providerCode);
    }
  }, [isOnlyProvider, paymentProvider, providerCode, setPaymentProvider]);

  const expandAndSetPaymentProvider = () => {
    if (paymentProvider !== providerCode) {
      setPaymentProvider(providerCode);
    } else {
      setPaymentProvider("");
    }
  };

  // Function to create a Rebill product wrapped in useCallback
  const createProduct = useCallback(async () => {
    if (!order.payment || !order.payment.public_id) {
      setError("No payment information available");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/rebill/${isExperience ? 'create-product/experience' : 'create-product'}`
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentPublicId: order.payment.public_id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setProductId(data.productId);
    } catch (e) {
      console.error("Error creating Rebill product:", e);
      setError("Failed to create payment product. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [order.payment, isExperience]);

  // Initialize Rebill SDK after script has loaded
  useEffect(() => {
    if (
      rebillScriptReady &&
      typeof window !== "undefined" &&
      window.Rebill &&
      !sdkMounted &&
      paymentProvider === providerCode
    ) {
      const public_key = !isUsProvider ? process.env.REACT_APP_REBILL_PUBLIC_KEY : process.env.REACT_APP_REBILL_PUBLIC_KEY_US;
      if (public_key) {
        const rebill = new window.Rebill(public_key);
        setSDKMounted(rebill);
      }
    }
  }, [rebillScriptReady, paymentProvider, providerCode, sdkMounted, isUsProvider]);

  // Automatically create product when Rebill is selected
  useEffect(() => {
    if (paymentProvider === providerCode && !productId && !loading) {
      createProduct();
    }
  }, [paymentProvider, providerCode, productId, loading, createProduct]);

  // Mount the iframe when product ID is available
  useEffect(() => {
    if (sdkMounted && productId) {
      const checkoutForm = sdkMounted.checkout.create({
        id: productId
      });

      checkoutForm.set({
        customerInformation: {
            firstName: order.customer.full_name.split(" ")[0],
            lastName: order.customer.full_name.split(" ")[1],
            email: order.customer.email,
            identification: {
                id: order.customer.id_number,
            },
        },
        billing: {
            line1: order.customer.address,
            zipCode: order.customer.zipcode,
        },
      })

      const excludedPaymentMethods = ["CASH"];
      if (isExperience) excludedPaymentMethods.push("TRANSFER");
    
      checkoutForm.display({
        userLogin: false,
        excludePaymentMethods: excludedPaymentMethods,
      });

      checkoutForm.set({
        paymentMetadata: {
            external_reference: order.payment.public_id,
            product_id: productId,
        }
      })

      checkoutForm.on('approved', async (event) => {
        // FIXME: we're not checking expireDate here because
        // 1. In the docs it says it's only for CASH payment methods
        // 2. The Boleto QR has a validity of 15 days, much higher of what we have in 
        // our business rule for expiring bookings
        const base64ImageString = event.paymentType === "alternative" ? event.data.slip.code.image : null;
        const paymentPublicId = order.payment.public_id;

        const checkShouldGoToEnjoy = async (retries = 10, delay = 3000, initialDelay = 30000) => {
          // Only wait for the initial delay if paymentType is not 'card'
          if (event.paymentType !== 'card') {
            await new Promise((resolve) => setTimeout(resolve, initialDelay));
          }
          for (let attempt = 0; attempt < retries; attempt++) {
            try {
              const response = await fetch(`${process.env.REACT_APP_API_URL}/rebill/should-go-to-enjoy`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  paymentPublicId,
                  ...(base64ImageString && { base64ImageString }),
                }),
              });

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              const data = await response.json();
              if (data.shouldGo) {
                navigate(`/checkout/enjoy?external_reference=${paymentPublicId}`);
                return;
              }
            } catch (error) {
              console.error("Error checking should-go-to-enjoy:", error);
            }

            // Wait before retrying
            await new Promise((resolve) => setTimeout(resolve, delay));
          }

          console.error("Max retries reached. Could not confirm should-go-to-enjoy.");
        };

        await checkShouldGoToEnjoy();
      });
      
      checkoutForm.mount("rebill");
    }
  }, [sdkMounted, productId, order, navigate, isExperience]);

  // Remove script from Helmet and load it programmatically
  useEffect(() => {
    if (window.Rebill) {
      setRebillScriptReady(true);
      return;
    }
    const scriptId = "rebill-sdk-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.src = "https://sdk.rebill.com/v3/rebill.js";
      script.async = true;
      script.id = scriptId;
      script.onload = () => {
        setRebillScriptReady(true);
      };
      document.body.appendChild(script);
    } else if (window.Rebill) {
      setRebillScriptReady(true);
    }
  }, []);

  // Unmount component after 1 hour
  useEffect(() => {
    const timer = setTimeout(() => setExpired(true), 3600000);
    return () => clearTimeout(timer);
  }, []);

  // If expired, render nothing
  if (expired) return null;

  // If Rebill is the only provider, always show expanded content
  const isExpanded = isOnlyProvider || paymentProvider === providerCode;

  return (
    <>
      <div className={`payments-dropdown__container${isExpanded ? ' selected' : ''}`}>
        <button
          className={`payments-dropdown__button${isExpanded ? ' selected' : ''}`}
          onClick={expandAndSetPaymentProvider}
          style={isOnlyProvider ? { pointerEvents: "none", opacity: 0.7 } : {}}
        >
          <div className="payments-dropdown__button-elements">
            <span className="icon">
              <CreditCard />
            </span>
            <span>
              {/* {PAYMENT_PROVIDER_INFO[providerCode].name} */}
              Completa el pago{isUsProvider ? ' en USD' : ''}
            </span>
          </div>
          <div className={isExpanded ? "dropdown__icon dropdown__icon--expanded" : "dropdown__icon"}>
            <ChevronRight />
          </div>
        </button>
        <div className={`payments-dropdown__form${isExpanded ? ' expanded' : ''}`}>
          <div className="payments-dropdown__form-content">
            <div id="rebill" className="rebill-container" style={{ 
              maxHeight: '500px', 
              overflow: 'auto', 
              width: '100%', 
              position: 'relative'
            }}>
              {loading && <div className="rebill-loading">Cargando opciones de pago...</div>}
              {error && <div className="rebill-error">{error}</div>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rebill;
